import { isAxiosError } from 'axios';
import axiosInstance from './axiosInstance';
import { resizeImageFile } from '../../utils/resizeImageFile';
import type {
  CreatorApplication,
  CreatorMyApplicationContext,
  SaveCreatorApplicationDraftPayload,
  SubmitCreatorApplicationPayload,
} from '../../types/creatorApplication.types';
import type {
  CreatorAnalyticsOverview,
  CreatorCampaign,
  CreatorCollection,
  CreatorDashboardOverview,
  CreatorLedgerRow,
  CreatorLinkAnalytics,
  CreatorPayout,
  CreatorPayoutMethod,
  CreatorProfile,
  CreatorReferralLinksResponse,
  CreatorSettings,
  CreatorStorefront,
  PublicCreatorStorefront,
  PublicCampaignContext,
  CreatorCommissionRow,
  CreatorAttributedOrder,
} from '../../types/creator.types';

export type CreatorWalletSnapshot = {
  pendingCommission: number;
  availableBalance: number;
  withdrawableBalance: number;
  lifetimeCommission: number;
  paidOut: number;
  payoutReserve: number;
  outstandingDebt: number;
};

export type CreatorFinanceOverview = {
  pendingBalance: number;
  availableBalance: number;
  availableGross?: number;
  payoutReserve?: number;
  outstandingDebt: number;
  lifetimeEarnings: number;
  totalPaidOut: number;
  negativeAdjustmentsTotal: number;
  postReleaseClawbacksTotal: number;
  currentMonthEarnings: number;
  nextAvailablePayoutAmount: number | null;
  nextAvailablePayoutDate: string | null;
  minPayoutAmount: number;
  protectionDays: number;
  walletBreakdown?: {
    availableGross: number;
    availableWithdrawable: number;
    payoutReserve: number;
    outstandingDebt: number;
  };
};

export type CreatorWalletSnapshotLine = {
  label: string;
  amount: number;
  tone: 'neutral' | 'positive' | 'negative' | 'emphasis';
  hint: string | null;
};

export type CreatorWalletCalculation = {
  asOf: string;
  pendingBalance: number;
  availableGross: number;
  payoutReserve: number;
  outstandingDebt: number;
  withdrawable: number;
  lifetimeEarnings: number;
  totalPaidOut: number;
  postReleaseClawbacksTotal: number;
  lines: CreatorWalletSnapshotLine[];
  openPayouts: Array<{ id: string; amount: number; status: string; createdAt: string }>;
};

export type CreatorWalletActivityItem = {
  id: string;
  kind: 'ledger' | 'payout';
  at: string;
  title: string;
  detail: string | null;
  orderNumber: string | null;
  transactionType: string | null;
  payoutStatus: string | null;
  pendingDelta: number;
  availableDelta: number;
  lifetimeDelta: number;
  amount: number | null;
  pendingBalanceAfter: number;
  availableGrossAfter: number;
  withdrawableAfter: number;
  payoutReserveAfter: number;
  outstandingDebtAfter: number;
};

export type { CreatorApplication };

function isRetryableUploadError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (!error.response) return true;
  return error.response.status === 503;
}

async function uploadCreatorImage(
  file: File,
  endpoint: string,
  fileNamePrefix: string,
  maxDimension: number,
): Promise<string> {
  const image = await resizeImageFile(file, maxDimension);
  const body = { image, fileName: `${fileNamePrefix}-${Date.now()}.jpg` };
  const config = { timeout: 130_000 };

  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await axiosInstance.post<{ url: string }>(endpoint, body, config);
      if (!data.url || data.url.startsWith('data:')) {
        throw new Error('Upload did not return a valid URL');
      }
      return data.url;
    } catch (error) {
      lastError = error;
      if (attempt === 0 && isRetryableUploadError(error)) continue;
      throw error;
    }
  }
  throw lastError;
}

export const creatorService = {
  /** Upload avatar to CDN; returns URL to store on the application. */
  uploadApplicationAvatar: (file: File) =>
    uploadCreatorImage(file, '/creator/applications/avatar/upload', 'creator-avatar', 512),

  /** Upload avatar to CDN; returns URL to store on the active creator profile. */
  uploadProfileAvatar: (file: File) =>
    uploadCreatorImage(file, '/creator/profile/avatar/upload', 'creator-avatar', 512),

  /** Upload banner to CDN; returns URL to store on the active creator profile. */
  uploadProfileBanner: (file: File) =>
    uploadCreatorImage(file, '/creator/profile/banner/upload', 'creator-banner', 1600),

  saveApplicationDraft: (payload: SaveCreatorApplicationDraftPayload) =>
    axiosInstance
      .put<CreatorApplication>('/creator/applications/draft', payload)
      .then((r) => r.data),

  submitApplication: (payload: SubmitCreatorApplicationPayload) =>
    axiosInstance.post<CreatorApplication>('/creator/applications', payload).then((r) => r.data),

  getMyApplication: () =>
    axiosInstance
      .get<CreatorMyApplicationContext>('/creator/applications/me')
      .then((r) => r.data),

  getProfile: () => axiosInstance.get<CreatorProfile>('/creator/profile').then((r) => r.data),

  updateProfile: (payload: Partial<CreatorProfile>) =>
    axiosInstance.put<CreatorProfile>('/creator/profile', payload).then((r) => r.data),

  getSettings: () => axiosInstance.get<CreatorSettings>('/creator/settings').then((r) => r.data),

  updateSettings: (payload: Partial<CreatorSettings>) =>
    axiosInstance.put<CreatorSettings>('/creator/settings', payload).then((r) => r.data),

  getWalletSnapshot: () =>
    axiosInstance.get<CreatorWalletSnapshot>('/creator/finance/wallet-snapshot').then((r) => r.data),

  getFinanceOverview: () =>
    axiosInstance.get<CreatorFinanceOverview>('/creator/finance/overview').then((r) => r.data),

  getFinanceWalletCalculation: () =>
    axiosInstance
      .get<CreatorWalletCalculation>('/creator/finance/wallet-calculation')
      .then((r) => r.data),

  getFinanceWalletActivity: (limit = 80) =>
    axiosInstance
      .get<{ items: CreatorWalletActivityItem[]; total: number }>(
        '/creator/finance/wallet-activity',
        { params: { limit: String(limit) } },
      )
      .then((r) => r.data),

  getAttributedOrderDetail: (orderId: string) =>
    axiosInstance
      .get<{ order: CreatorAttributedOrder; lines: CreatorCommissionRow[] }>(
        `/creator/finance/orders/${orderId}`,
      )
      .then((r) => r.data),

  getDashboardOverview: async (): Promise<CreatorDashboardOverview> => {
    try {
      const { data } = await axiosInstance.get<CreatorDashboardOverview>('/creator/dashboard/overview');
      return data;
    } catch {
      const [profile, wallet, analytics] = await Promise.all([
        creatorService.getProfile(),
        creatorService.getWalletSnapshot(),
        creatorService.getAnalyticsOverview().catch(() => ({
          clicks: 0,
          conversions: 0,
          conversionRate: 0,
          revenueAttributed: 0,
          commissionEarned: 0,
        })),
      ]);
      return { profile, wallet, analytics };
    }
  },

  getReferralLinks: () =>
    axiosInstance
      .get<CreatorReferralLinksResponse>('/creator/referrals/links')
      .then((r) => r.data),

  createReferralLink: (payload: {
    linkType: 'PRODUCT' | 'COLLECTION' | 'CUSTOM' | 'STOREFRONT';
    targetPath?: string;
    productId?: string;
    collectionId?: string;
  }) => axiosInstance.post('/creator/referrals/links', payload).then((r) => r.data),

  deleteReferralLink: (id: string) =>
    axiosInstance.delete(`/creator/referrals/links/${id}`).then((r) => r.data),

  getStorefront: () =>
    axiosInstance.get<CreatorStorefront>('/creator/storefront').then((r) => r.data),

  updateStorefront: (payload: Partial<CreatorStorefront>) =>
    axiosInstance.put<CreatorStorefront>('/creator/storefront', payload).then((r) => r.data),

  getCollections: () =>
    axiosInstance.get<{ collections: CreatorCollection[] }>('/creator/collections').then((r) => r.data),

  createCollection: (payload: { title: string; slug?: string; description?: string; productIds?: string[] }) =>
    axiosInstance.post<CreatorCollection>('/creator/collections', payload).then((r) => r.data),

  updateCollection: (id: string, payload: Partial<CreatorCollection>) =>
    axiosInstance.put<CreatorCollection>(`/creator/collections/${id}`, payload).then((r) => r.data),

  deleteCollection: (id: string) =>
    axiosInstance.delete(`/creator/collections/${id}`).then((r) => r.data),

  getCampaigns: () =>
    axiosInstance
      .get<{ campaigns: CreatorCampaign[]; availableCount?: number; joinedCount?: number }>(
        '/creator/campaigns',
      )
      .then((r) => r.data),

  joinCampaign: (id: string) =>
    axiosInstance.post(`/creator/campaigns/${id}/join`).then((r) => r.data),

  getAnalyticsOverview: (params?: { period?: string }) =>
    axiosInstance
      .get<CreatorAnalyticsOverview>('/creator/analytics/overview', { params })
      .then((r) => r.data),

  getAnalyticsLinks: (params?: { period?: string }) =>
    axiosInstance
      .get<{ links: CreatorLinkAnalytics[] }>('/creator/analytics/links', { params })
      .then((r) => r.data),

  getLedger: (params?: {
    limit?: string;
    offset?: string;
    from?: string;
    to?: string;
    orderId?: string;
    transactionTypes?: string;
  }) =>
    axiosInstance
      .get<{ rows: CreatorLedgerRow[]; total: number }>('/creator/finance/ledger', { params })
      .then((r) => r.data),

  getFinanceAdjustments: () =>
    axiosInstance
      .get<CreatorLedgerRow[]>('/creator/finance/adjustments')
      .then((r) => r.data),

  getFinanceCommissions: (params?: {
    limit?: string;
    offset?: string;
    from?: string;
    to?: string;
    balanceStatus?: string;
    designSearch?: string;
  }) =>
    axiosInstance
      .get<{ rows: CreatorCommissionRow[]; total: number }>('/creator/finance/commissions', {
        params,
      })
      .then((r) => r.data),

  getAttributedOrders: (params?: { limit?: string; offset?: string }) =>
    axiosInstance
      .get<{ orders: CreatorAttributedOrder[]; total: number }>('/creator/finance/orders', {
        params,
      })
      .then((r) => r.data),

  getPayouts: () =>
    axiosInstance.get<{ payouts: CreatorPayout[] }>('/creator/finance/payouts').then((r) => r.data),

  requestPayout: (payload: { amount: number; payoutMethodId?: string }) =>
    axiosInstance.post('/creator/finance/payouts', payload).then((r) => r.data),

  getPayoutMethods: () =>
    axiosInstance
      .get<{ methods: CreatorPayoutMethod[] }>('/creator/payout-methods')
      .then((r) => r.data),

  createPayoutMethod: (payload: {
    methodType: string;
    label?: string;
    details: Record<string, unknown>;
    isDefault?: boolean;
  }) => axiosInstance.post<CreatorPayoutMethod>('/creator/payout-methods', payload).then((r) => r.data),

  deletePayoutMethod: (id: string) =>
    axiosInstance.delete(`/creator/payout-methods/${id}`).then((r) => r.data),

  setDefaultPayoutMethod: (id: string) =>
    axiosInstance
      .patch<CreatorPayoutMethod>(`/creator/payout-methods/${id}/default`)
      .then((r) => r.data),

  getPublicStorefront: (slug: string) =>
    axiosInstance
      .get<PublicCreatorStorefront>(`/public/creator/storefront/${encodeURIComponent(slug)}`)
      .then((r) => r.data),

  getPublicCampaignContext: (campaignId: string, slug?: string) =>
    axiosInstance
      .get<{ campaign: PublicCampaignContext }>(
        `/public/creator-campaigns/${encodeURIComponent(campaignId)}/context`,
        { params: slug ? { slug } : undefined },
      )
      .then((r) => r.data.campaign),

  getOnboardingStatus: () => axiosInstance.get('/creator/onboarding/status').then((r) => r.data),

  updateOnboardingStep: (payload: Record<string, unknown>) =>
    axiosInstance.post('/creator/onboarding/step', payload).then((r) => r.data),

  completeOnboarding: () => axiosInstance.post('/creator/onboarding/complete').then((r) => r.data),

  skipOnboardingStep: (step: string) =>
    axiosInstance.post('/creator/onboarding/skip', { step }).then((r) => r.data),

  getNotifications: (params?: { limit?: string; offset?: string; unreadOnly?: boolean }) =>
    axiosInstance
      .get<{ items: CreatorNotification[]; total: number; unread: number }>(
        '/creator/growth/notifications',
        {
          params: {
            ...params,
            unreadOnly: params?.unreadOnly ? 'true' : undefined,
          },
        },
      )
      .then((r) => r.data),

  markNotificationRead: (id: string) =>
    axiosInstance.patch(`/creator/growth/notifications/${id}/read`).then((r) => r.data),

  markAllNotificationsRead: () =>
    axiosInstance.post('/creator/growth/notifications/read-all').then((r) => r.data),

  getAchievements: () =>
    axiosInstance
      .get<{ achievements: CreatorAchievement[] }>('/creator/growth/achievements')
      .then((r) => r.data),

  getTierProgress: () =>
    axiosInstance.get<CreatorTierProgress>('/creator/growth/tier-progress').then((r) => r.data),

  getLeaderboards: (period?: string) =>
    axiosInstance
      .get<CreatorLeaderboardResult[]>('/creator/growth/leaderboard', { params: { period } })
      .then((r) => r.data),

  getInsights: () =>
    axiosInstance
      .get<{ insights: CreatorInsightCard[] }>('/creator/growth/insights')
      .then((r) => r.data),

  getAssets: (params?: { campaignId?: string; category?: string; search?: string }) =>
    axiosInstance.get<{ assets: CreatorAsset[] }>('/creator/assets', { params }).then((r) => r.data),

  getAnalyticsFunnel: (period?: string) =>
    axiosInstance
      .get<CreatorAnalyticsFunnel>('/creator/analytics/funnel', { params: { period } })
      .then((r) => r.data),

  getAnalyticsBreakdown: (period?: string) =>
    axiosInstance
      .get('/creator/analytics/insights', { params: { period } })
      .then((r) => r.data),
};

export type CreatorNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  href: string;
  metadata: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
};

export type CreatorAchievement = {
  code: string;
  title: string;
  description: string;
  unlocked: boolean;
  unlockedAt: string | null;
  progress: number | null;
  target: number | null;
};

export type CreatorTierProgress = {
  current: string;
  currentSlug: string;
  bonusPercent: number;
  effectiveCommissionPercent: number;
  nextTier: string | null;
  nextTierSlug: string | null;
  nextTierMinOrders: number | null;
  nextTierMinRevenue: number;
  nextTierReward: string | null;
  ordersCount: number;
  revenueGenerated: number;
  ordersProgressPercent: number;
  revenueProgressPercent: number;
  lastPromotedAt: string | null;
};

export type CreatorLeaderboardResult = {
  board: string;
  period: string;
  entries: Array<{
    rank: number;
    creatorId: string;
    displayName: string;
    slug: string;
    value: number;
    label: string;
  }>;
  self: {
    rank: number;
    creatorId: string;
    displayName: string;
    slug: string;
    value: number;
    label: string;
  } | null;
};

export type CreatorInsightCard = {
  id: string;
  title: string;
  body: string;
  href?: string;
  tone: 'info' | 'success' | 'warning';
};

export type CreatorAsset = {
  id: string;
  campaignId: string | null;
  title: string;
  assetType: string;
  url: string;
  caption: string | null;
  hashtags: string | null;
  usageInstructions: string | null;
  category: string | null;
  source: 'library' | 'campaign';
};

export type CreatorAnalyticsFunnel = {
  clicks: number;
  storefrontViews: number;
  productViews: number | null;
  addToCart: number;
  checkoutStarted: number;
  purchases: number;
  conversionRate: number;
  period: string;
};
